const API_BASE_URL = (
  import.meta.env.VITE_API_BASE_URL ??
  (import.meta.env.DEV ? "http://localhost:8787" : "")
).replace(/\/+$/g, "");

if (!API_BASE_URL) {
  throw new Error("生产构建缺少 VITE_API_BASE_URL");
}

const TOKEN_KEY = "cf-file-control.session";

export class ApiClientError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = "ApiClientError";
  }
}

export function getStoredToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function storeToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearStoredToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

export async function apiRequest<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  return request<T>(path, init, true);
}

export async function publicApiRequest<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  return request<T>(path, init, false);
}

async function request<T>(
  path: string,
  init: RequestInit,
  authenticated: boolean,
): Promise<T> {
  const headers = new Headers(init.headers);
  const token = authenticated ? getStoredToken() : null;
  if (token) {
    headers.set("Authorization", `Bearer ${token}`);
  }
  if (init.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers,
  });
  if (response.status === 204) {
    return undefined as T;
  }

  const payload = (await response.json().catch(() => null)) as
    | {
        error?: { code?: string; message?: string };
      }
    | null;
  if (!response.ok) {
    const error = new ApiClientError(
      response.status,
      payload?.error?.code ?? "REQUEST_FAILED",
      payload?.error?.message ?? `请求失败（${response.status}）`,
    );
    if (
      authenticated &&
      response.status === 401 &&
      path !== "/api/auth/login"
    ) {
      clearStoredToken();
      window.dispatchEvent(new Event("auth-expired"));
    }
    throw error;
  }
  return payload as T;
}

export function putFileDirect(
  url: string,
  headers: Record<string, string>,
  file: File,
  onProgress: (percent: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open("PUT", url);
    Object.entries(headers).forEach(([name, value]) => {
      request.setRequestHeader(name, value);
    });
    request.upload.addEventListener("progress", (event) => {
      if (event.lengthComputable) {
        onProgress(Math.round((event.loaded / event.total) * 100));
      }
    });
    request.addEventListener("load", () => {
      if (request.status >= 200 && request.status < 300) {
        resolve();
      } else {
        reject(new Error(`文件上传失败（${request.status}）`));
      }
    });
    request.addEventListener("error", () => {
      reject(new Error("无法连接到存储服务，请联系管理员"));
    });
    request.addEventListener("abort", () => reject(new Error("上传已取消")));
    request.send(file);
  });
}
